import styled from 'styled-components';
import { useScreenQuery } from 'playup-web-core';
import React from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { noop } from 'lodash';

import ReusableModalPlain from '../../../components/ReusableModal/ReusableModalPlain';
import DepositLimitHeader from './DepositLimitHeader';
import { useOnboardingDepositScreenState } from './useOnboardingDepositScreenState';
import PromotionItem from './PromotionItem';
import CreditCardInput from './CreditCardInput';
import CreditCard from './CreditCard/CreditCard';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { useTutorialContextProvider } from '../../../contexts/TutorialContext';

enum Step {
	Amount,
	CreditCard,
}

const OnboardingDepositModal = ({ onClose }) => {
	const { isSmall } = useScreenQuery();
	const { onSelectPromotion, selectedPromotion, depositPromotions, amount, onAmountChange, error, handleSubmit } =
		useOnboardingDepositScreenState();
	const { user, refreshPortalUser } = React.useContext(PortalSessionContext);
	const [step, setStep] = React.useState<Step>(Step.Amount);
	const onSubmit = React.useCallback((data) => {
		setStep(Step.CreditCard);
	}, []);
	const { openTutorialModal } = useTutorialContextProvider();

	const handleClose = () => {
		onClose();
		openTutorialModal();
	};

	const onGoBack = React.useCallback(() => {
		setStep(Step.Amount);
	}, []);

	return (
		<StyledModal isSmallScreen={isSmall}>
			<ModalHeader isSmallScreen={isSmall}>
				<ModalHeaderItem>
					{step === Step.CreditCard && (
						<IoIosArrowBack onClick={onGoBack} style={{ fontSize: 20, fontWeight: 'bold' }} />
					)}
				</ModalHeaderItem>
				<ModalTitle>Deposit Funds</ModalTitle>
				<ModalHeaderItem></ModalHeaderItem>
			</ModalHeader>
			<DepositLimitHeader />
			<Content isSmallScreen={isSmall}>
				{step === Step.Amount ? (
					<>
						<WelcomeText>Welcome {user?.name}!</WelcomeText>
						<DescriptionText>
							Your account has been successfully created! Deposit funds using one of the instant options below to start
							betting immediately
						</DescriptionText>
						{depositPromotions.length > 0 && <SubTitle>Special Offers</SubTitle>}

						{depositPromotions.map((item) => {
							return (
								<PromotionItem
									key={item.id}
									onSelectPromotion={() => onSelectPromotion(item)}
									isSelected={selectedPromotion?.id === item.id}
									neverExpire={item.rules.never_expire}
									expiredAt={item.expired_at}
									maxBonusAmount={item.rules.max_bonus_amount}
									title={item.name}
									brandId={item.client.id}
								/>
							);
						})}
						<SubTitle>Instant Deposit Options</SubTitle>
						<CreditCardInput error={error} amount={amount} onAmountChange={onAmountChange} />
						<SubmitButton onClick={handleSubmit(onSubmit)}>Deposit</SubmitButton>
						<CancelButton onClick={handleClose}>Deposit later</CancelButton>
					</>
				) : (
					<CreditCard
						setAmountError={noop}
						userUpdater={refreshPortalUser}
						selectedPromotion={selectedPromotion}
						amount={amount}
						close={onClose}
						onTryAgain={onGoBack}
						isOnboarding
					/>
				)}
			</Content>
		</StyledModal>
	);
};

const CancelButton = styled.a`
	text-decoration: underline;
	font-size: 14px;
	cursor: pointer;
	text-align: center;
	color: #000;
`;

const SubmitButton = styled.button`
	background-color: black;
	width: 100%;
	height: 50px;
	margin-top: 22px;
	border-radius: 6px;
	color: white;
	font-weight: bold;
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 22px;
`;

const WelcomeText = styled.span`
	font-size: 15px;
	font-weight: bold;
`;

const DescriptionText = styled.span`
	font-size: 14px;
	margin-top: 8px;
	margin-bottom: 22px;
	font-weight: 500;
`;

const SubTitle = styled.span`
	font-size: 14px;
	margin-bottom: 9px;
	font-weight: 500;
`;

const Content = styled.div<{ isSmallScreen: boolean }>`
	background-color: #fff;
	maxheight: calc(100% - 40px - 56px);
	margin-top: -10px;
	border-radius: 6px;
	color: #000;
	display: flex;
	flex-direction: column;
	padding-left: 8px;
	padding-right: 8px;
	padding-top: 10px;
	padding-bottom: 10px;
	margin-left: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '0px')};
	margin-right: ${({ isSmallScreen }) => (isSmallScreen ? '12px' : '0px')};
`;

const ModalHeader = styled.div<{ isSmallScreen: boolean }>`
	height: 56px;
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	flex-wrap: wrap;
	align-items: center;
`;

const ModalHeaderItem = styled.div`
	display: flex;
	flex: 1;
`;

const ModalTitle = styled.h4`
	margin: 0;
	color: #000;
	font-size: 20px;
	line-height: 24px;
	font-weight: bolder;
	text-align: center;
	color: #fff;
	display: flex;
	flex: 1;
	text-align: center;
	justify-content: center;
`;

const StyledModal = styled(ReusableModalPlain)<{ isSmallScreen: boolean }>`
	overflow-y: scroll;
	min-width: 416px;
	min-height: 350px;
	width: 100vw;
	padding: 12px;
	max-width: 100%;
	position: fixed;
	background-color: #191d22;
	scrollbar-display: none;
	${({ isSmallScreen }) => (isSmallScreen ? 'width: 100vw; height: 100vh;' : 'width: 30vw; 	max-height: 100vh;')}
`;

export default OnboardingDepositModal;
